import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import {
  createRootFolder,
  getContentLibrary,
  normalizeContentLibrary,
  normalizeFile,
  normalizeFolder,
} from 'features/content-library/utils/normalizeContentLibrary';
import { handleFileProcessing } from 'features/content-library/utils/handleFileProcessing';
import { getAllFolderIds } from 'features/content-library/utils/gatherContentLibraryItems';
import {
  promoteRevealById,
  promoteRevealByLocation,
} from 'features/content-library/utils/contentLibraryReveal';
import {
  bulkFilesClosed,
  createContentFolderRequested,
  createEmbedContentRequested,
  fileViewed,
  signalingContentLibraryConversionFailed,
  signalingContentLibraryConversionProgress,
  signalingContentLibraryEmbedContentCreated,
  signalingContentLibraryFileClosed,
  signalingContentLibraryFileConverted,
  signalingContentLibraryFileCopied,
  signalingContentLibraryFileDeleted,
  signalingContentLibraryFileMoved,
  signalingContentLibraryFileRenamed,
  signalingContentLibraryFileShared,
  signalingContentLibraryFileUnshared,
  signalingContentLibraryFileUploadAccepted,
  signalingContentLibraryFileUploaded,
  signalingContentLibraryFolderCreated,
  signalingContentLibraryFolderDeleted,
  signalingContentLibraryFolderMoved,
  signalingContentLibraryFolderRenamed,
  signalingContentLibraryOpenedFilesReceived,
  signalingContentLibrarySharedFilesReceived,
  signalingLibraryReceived,
  whiteboardViewed,
} from 'features/content-library/actions';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import {
  bulkWhiteboardsClosed,
  signalingWhiteboardClosed,
  signalingWhiteboardCreated,
} from 'features/layout/features/content/actions';
import { isContentLibraryMediaPlayer } from 'features/content-library/utils/isContentLibraryMediaPlayer';
import { signalingWhiteboardModeChanged } from 'features/whiteboard/actions';
import {
  CONTENT_LIBRARY_SORT_BY_STORAGE_KEY,
  SHARED_FOLDER_ID,
  PLAYER_FIELD_BY_INTERACTION_TYPE,
} from 'features/content-library/constants';
import i18n from 'i18n';
import {
  ContentLibraryFileEntry,
  ContentLibraryFileProcessingMeta,
  ContentLibraryFolder,
  ContentLibraryOpenedFile,
  ContentLibraryOpenedWhiteboard,
  ContentLibraryPageChangedPayload,
  ContentLibrarySortBy,
  ContentLibraryState,
  ContentLibraryTemplateParams,
  ContentLibraryType,
  ContentLibraryMediaPlayerStateChangePayload,
  ContentLibraryMediaPlayerRemotePayload,
} from 'features/content-library/types';
import { safeLocalStorage } from 'utils/storage';
import {
  formatUrlParams,
  replaceUrlParams,
  rewriteGoogleEmbedUrl,
} from 'features/content-library/utils/urlParams';
import { normalizeSharedFiles } from 'features/content-library/utils/normalizeSharedFiles';
import { formatWhiteboards } from 'features/content-library/utils/format';
import {
  closeActiveFile,
  closeActiveWhiteboard,
  findAndUpdateFileInLibraries,
  openedFileInitialState,
} from 'features/content-library/utils/slice-utils';
import {
  selectCanManagePrivateWhiteboards,
  selectHasPermissions,
} from 'features/permissions/permissionsSlice';
import { PermissionTypes } from 'features/permissions/types';
import { userRenamed } from 'features/users/actions';
import { buildPlayerUpdates } from 'features/content-library/utils/buildPlayerUpdates';

const createLibraryInitialState = (personal: boolean) => ({
  libraryId: undefined,
  activeFolderId: 'root',
  folders: {
    root: createRootFolder(personal),
  },
  files: {},
  fileProcessingMeta: {},
});

const isContentLibrarySortBy = (value: string | null): value is ContentLibrarySortBy =>
  value === 'name' || value === 'dateModified';

const getStoredSortBy = (): ContentLibrarySortBy => {
  const stored = safeLocalStorage.getItem(CONTENT_LIBRARY_SORT_BY_STORAGE_KEY);

  return isContentLibrarySortBy(stored) ? stored : 'name';
};

export const initialState: ContentLibraryState = {
  openedFile: {
    ...openedFileInitialState,
  },
  previewedFile: null,
  showLoader: false,
  activeFiles: {},
  activeWhiteboards: {},
  currentActiveFileId: null,
  openedFileThumbnailsActive: false,
  activeLibraryType: 'room',
  sortBy: getStoredSortBy(),
  pendingReveal: null,
  revealItemId: null,
  originalDefaultLibraryType: null,
  libraries: {
    personal: createLibraryInitialState(true),
    room: createLibraryInitialState(false),
  },
  mediaPlayers: {},
  templateParams: {},
  sharedFiles: {
    folder: {
      id: SHARED_FOLDER_ID,
      name: i18n.t('common:shared_content_folder_name'),
      parentId: 'root',
      childFolderIds: [],
      childFileIds: [],
    },
    files: {},
  },
};

export const contentLibrary = createSlice({
  name: 'contentLibrary',
  initialState,
  reducers: {
    loaderDisplayChanged(state, action: PayloadAction<boolean>) {
      state.showLoader = action.payload;
    },
    folderOpened(state, action: PayloadAction<string>) {
      state.libraries[state.activeLibraryType].activeFolderId = action.payload;
    },
    fileUploadProgressChanged(state, action: PayloadAction<{ id: string; progress: number }>) {
      const meta = state.libraries[state.activeLibraryType].fileProcessingMeta[action.payload.id];
      if (meta) {
        meta.progress = action.payload.progress;
      }
    },
    fileOpened(state, action: PayloadAction<ContentLibraryOpenedFile>) {
      const fileData = { ...action.payload };

      state.openedFile = {
        data: fileData,
        currentPage: fileData.page ?? 0,
      };

      if (isContentLibraryMediaPlayer(fileData)) {
        state.mediaPlayers[fileData.id] = {
          id: fileData.id,
          isReady: false,
          state: 'idle',
          currentTime: 0,
          inboundCounter: 0,
          muted: false,
          volume: null,
        };
      }

      if (fileData.type !== 'whiteboard') {
        if (!state.activeFiles[fileData.id]) {
          state.activeFiles[fileData.id] = fileData;
        }

        if (state.activeFiles[fileData.id]) {
          if (fileData.page !== undefined) {
            state.activeFiles[fileData.id].page = fileData.page;
          }

          if (fileData.url) {
            state.activeFiles[fileData.id].url = fileData.url;
          }
        }
      }

      if (fileData.type === 'whiteboard' && !state.activeWhiteboards[fileData.id]) {
        state.activeWhiteboards[fileData.id] = fileData as ContentLibraryOpenedWhiteboard;
      }

      if ('pages' in fileData) {
        state.openedFile.currentPage = fileData.page ?? 0;
      }

      if (state.openedFile.data && fileData.type === 'webapp') {
        state.openedFile.data.url = encodeURI(
          replaceUrlParams(
            formatUrlParams(rewriteGoogleEmbedUrl(fileData.url), 'decode'),
            state.templateParams
          )
        );
      }
    },
    fileClosed(state, action: PayloadAction<string>) {
      closeActiveFile(state, action.payload);

      // @TODO cleanup video player
    },
    filePageChanged(state, action: PayloadAction<ContentLibraryPageChangedPayload>) {
      const data = action.payload;

      if (state.openedFile.data && state.openedFile.data.id === data.id) {
        if ('pages' in state.openedFile.data) {
          state.openedFile.data.page = data.page;
        } else {
          state.openedFile.data = {
            ...state.openedFile.data,
            ...data,
          };
        }

        state.openedFile.currentPage = data.page;
      }

      if (state.activeFiles[data.id]) {
        state.activeFiles[data.id].page = data.page;
        if (data.url) {
          state.activeFiles[data.id].url = data.url;
        }
      }
    },
    openedFileCleared(state) {
      state.openedFile = {
        ...openedFileInitialState,
      };
    },
    previewFileOpened(state, action: PayloadAction<ContentLibraryOpenedFile>) {
      state.previewedFile = { ...action.payload };
    },
    previewFileClosed(state) {
      state.previewedFile = null;
    },
    previewPageChanged(state, action: PayloadAction<ContentLibraryPageChangedPayload>) {
      const { id, page, url } = action.payload;
      const previewed = state.previewedFile;

      if (!previewed || previewed.id !== id) {
        return;
      }

      if ('pages' in previewed) {
        previewed.page = page;
      } else if (url) {
        state.previewedFile = { ...previewed, page, url };
      } else {
        state.previewedFile = { ...previewed, page };
      }
    },
    activeFilesCollectionUpdated(
      state,
      action: PayloadAction<Record<string, ContentLibraryOpenedFile>>
    ) {
      const filteredFiles: Record<string, ContentLibraryOpenedFile> = {};

      for (const [id, file] of Object.entries(action.payload)) {
        const fileData = { ...file };

        filteredFiles[id] = fileData;
      }

      state.activeFiles = {
        ...state.activeFiles,
        ...filteredFiles,
      };
    },
    activeFilesCollectionCleared(state) {
      state.activeFiles = {};
    },
    activeWhiteboardsCollectionReplaced(
      state,
      action: PayloadAction<Record<string, ContentLibraryOpenedWhiteboard>>
    ) {
      state.activeWhiteboards = formatWhiteboards(action.payload);
    },
    activeWhiteboardsCollectionUpdated(
      state,
      action: PayloadAction<Record<string, ContentLibraryOpenedWhiteboard>>
    ) {
      state.activeWhiteboards = {
        ...state.activeWhiteboards,
        ...formatWhiteboards(action.payload),
      };
    },
    activeWhiteboardsCollectionCleared(state) {
      state.activeWhiteboards = {};
    },
    fileThumbnailsToggled(state) {
      state.openedFileThumbnailsActive = !state.openedFileThumbnailsActive;
    },
    fileUploadFailed(state, action: PayloadAction<{ id: string }>) {
      const processingMeta =
        state.libraries[state.activeLibraryType].fileProcessingMeta[action.payload.id];
      if (processingMeta) {
        processingMeta.status = 'uploading-failed';
      }
    },
    embedPlayerDeleted(state, action: PayloadAction<{ id: string }>) {
      if (state.mediaPlayers[action.payload.id]) {
        delete state.mediaPlayers[action.payload.id];
      }
    },
    embedPlayerUserAction: {
      reducer: (state, action: PayloadAction<ContentLibraryMediaPlayerStateChangePayload>) => {
        state.mediaPlayers[action.payload.id] ??= {
          id: action.payload.id,
          isReady: false,
          state: 'idle',
          currentTime: 0,
          inboundCounter: 0,
          muted: false,
          volume: null,
        };
        const player = state.mediaPlayers[action.payload.id];

        const { type, ...payload } = action.payload;

        // Post-end pause guard: browsers fire a spurious 'paused' after 'ended' — ignore it.
        if (player.state === 'ended' && payload.state === 'paused') {
          return;
        }

        state.mediaPlayers[payload.id] = { ...player, ...payload };
      },
      prepare: (payload: ContentLibraryMediaPlayerStateChangePayload) => {
        if (payload.currentTime !== undefined) {
          return {
            payload: { ...payload, currentTime: Math.round(payload.currentTime) },
            meta: {},
          };
        }
        return { payload, meta: {} };
      },
    },
    embedPlayerStateApplied: {
      reducer: (
        state,
        action: PayloadAction<
          ContentLibraryMediaPlayerRemotePayload,
          string,
          { source: 'live' | 'snapshot' | 'local' }
        >
      ) => {
        state.mediaPlayers[action.payload.id] ??= {
          id: action.payload.id,
          isReady: false,
          state: 'idle',
          currentTime: 0,
          inboundCounter: 0,
          muted: false,
          volume: null,
        };
        const player = state.mediaPlayers[action.payload.id];
        const { source } = action.meta;

        if (source === 'local') {
          // Monitor first-tick seed: apply only the single field for payload.type.
          // Does not bump inboundCounter — mirrors existing SDK state, no imperative write needed.
          const field = PLAYER_FIELD_BY_INTERACTION_TYPE[action.payload.type];
          const value = action.payload[field as keyof typeof action.payload];
          if (value !== undefined && value !== null) {
            // @ts-expect-error — field/value relation is known via PLAYER_FIELD_BY_INTERACTION_TYPE
            player[field] = value;
          }
          return;
        }

        const updates = buildPlayerUpdates(action.payload, source);

        // Bug N: bump inboundCounter on every non-local apply so the apply useEffect
        // re-arms its 150 ms cascade-absorption window for each inbound event.
        player.inboundCounter += 1;

        state.mediaPlayers[action.payload.id] = { ...player, ...updates };
      },
      prepare: (
        payload: ContentLibraryMediaPlayerRemotePayload,
        meta: { source: 'live' | 'snapshot' | 'local' }
      ) => ({
        payload: { ...payload, currentTime: Math.round(payload.currentTime) },
        meta,
      }),
    },
    embedPlayerReady(
      state,
      action: PayloadAction<{
        id: string;
      }>
    ) {
      const player = state.mediaPlayers[action.payload.id];
      if (player) {
        player.isReady = true;
      }
    },
    activeLibraryChanged(state, action: PayloadAction<ContentLibraryType>) {
      state.activeLibraryType = action.payload;
    },
    contentLibrarySortByChanged(state, action: PayloadAction<ContentLibrarySortBy>) {
      state.sortBy = action.payload;
    },
    templateParamsChanged(state, action: PayloadAction<ContentLibraryTemplateParams>) {
      state.templateParams = {
        ...state.templateParams,
        ...action.payload,
      };
    },
    currentActiveFileUpdated(state, action: PayloadAction<string>) {
      state.currentActiveFileId = action.payload;
    },
    sharedFolderLocalized(state, action: PayloadAction<string>) {
      state.sharedFiles.folder.name = action.payload;
    },
    sharedFilesCleared(state) {
      for (const id of state.sharedFiles.folder.childFileIds) {
        findAndUpdateFileInLibraries(state, id, (file) => {
          file.shared = false;
        });
      }

      state.sharedFiles.files = {};
      state.sharedFiles.folder.childFolderIds = [];
      state.sharedFiles.folder.childFileIds = [];

      if (state.libraries[state.activeLibraryType].activeFolderId === SHARED_FOLDER_ID) {
        state.libraries[state.activeLibraryType].activeFolderId = 'root';
      }
    },
    revealItemCleared(state) {
      state.revealItemId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingWhiteboardModeChanged, (state, action) => {
        const {
          data: { id, private: isPrivate },
        } = action.payload;

        if (state.activeWhiteboards[id]) {
          state.activeWhiteboards[id].private = isPrivate;
          state.activeWhiteboards[id].type = 'whiteboard';
        }

        if (state.openedFile.data && state.openedFile.data.id === id) {
          if ('private' in state.openedFile.data) {
            state.openedFile.data.private = isPrivate;
            state.openedFile.data.type = 'whiteboard';
          }
        }

        if (state.sharedFiles.files && state.sharedFiles.files[id]) {
          if ('private' in state.sharedFiles.files[id]) {
            state.sharedFiles.files[id].private = isPrivate;
          }
        }

        for (const libraryType of Object.keys(state.libraries)) {
          const library = state.libraries[libraryType as ContentLibraryType];
          if (library.files[id]) {
            if ('private' in library.files[id]) {
              library.files[id].private = isPrivate;
              library.files[id].type = 'whiteboard';
            }
          }
        }
      })
      .addCase(signalingLibraryReceived, (state, action) => {
        const libraryType: ContentLibraryType = action.payload.personal ? 'personal' : 'room';
        if (action.payload.id === null) {
          state.libraries[libraryType] = createLibraryInitialState(action.payload.personal);
          state.libraries[libraryType].libraryId = null;

          return state;
        }

        const { folders, files, fileProcessingMeta } = normalizeContentLibrary(
          state,
          action.payload
        );

        state.libraries[libraryType].folders = folders;
        state.libraries[libraryType].files = files;
        state.libraries[libraryType].libraryId = action.payload.id;
        state.libraries[libraryType].fileProcessingMeta = fileProcessingMeta;
      })
      .addCase(createContentFolderRequested, (state, action) => {
        const { name, parentId, personal } = action.payload;

        state.pendingReveal = { name, parentFolderId: parentId || 'root', personal };
      })
      .addCase(signalingContentLibraryFolderCreated, (state, action) => {
        const library = getContentLibrary(state, action.payload.personal);
        const folder = normalizeFolder(action.payload);

        const { folders } = library;
        folders[folder.id] = folder;
        if (folders[folder.parentId]) {
          folders[folder.parentId].childFolderIds.push(folder.id);
        }

        promoteRevealByLocation(state, {
          id: folder.id,
          name: folder.name,
          parentFolderId: folder.parentId,
          personal: folder.personal,
        });
      })
      .addCase(signalingContentLibraryFolderRenamed, (state, action) => {
        const library = getContentLibrary(state, action.payload.personal);
        const folder = library.folders[action.payload.id];
        if (folder) {
          folder.name = action.payload.name;
        }
      })
      .addCase(signalingContentLibraryFileRenamed, (state, action) => {
        const { id, name, personal } = action.payload;
        const library = getContentLibrary(state, personal);
        const file = library.files[id];
        if (file) {
          file.name = name;
        }

        if (state.sharedFiles.files[id]) {
          state.sharedFiles.files[id].name = name;
        }

        if (id === state.openedFile?.data?.id) {
          state.openedFile.data.name = name;
        }
        if (state.activeFiles[action.payload.id]) {
          state.activeFiles[id].name = name;
        }
        if (state.activeWhiteboards[id]) {
          state.activeWhiteboards[id].name = name;
        }
      })
      .addCase(signalingContentLibraryFileMoved, (state, action) => {
        const { id, parentId, personal } = action.payload;
        const library = getContentLibrary(state, personal);

        const file = library.files[id];
        if (!file) {
          return;
        }

        const targetId = parentId ?? 'root';
        const target = library.folders[targetId];
        if (!target) {
          return;
        }

        const source = library.folders[file.folderId];
        if (source) {
          source.childFileIds = source.childFileIds.filter((childId) => childId !== id);
        }

        file.folderId = targetId;
        target.childFileIds.push(id);
      })
      .addCase(signalingContentLibraryFolderMoved, (state, action) => {
        const { id, parentId, personal } = action.payload;
        const library = getContentLibrary(state, personal);

        const folder = library.folders[id];
        if (!folder) {
          return;
        }

        const targetId = parentId ?? 'root';
        const target = library.folders[targetId];
        if (!target) {
          return;
        }

        // A malformed broadcast re-parenting a folder into its own subtree would detach the
        // branch and strand every file beneath it. getAllFolderIds includes the folder itself.
        if (getAllFolderIds(library.folders, id).includes(targetId)) {
          return;
        }

        const source = library.folders[folder.parentId];
        if (source) {
          source.childFolderIds = source.childFolderIds.filter((childId) => childId !== id);
        }

        folder.parentId = targetId;
        target.childFolderIds.push(id);
      })
      .addCase(signalingContentLibraryFileDeleted, (state, action) => {
        const { id: fileId, personal } = action.payload;
        const library = getContentLibrary(state, personal);
        const file = library.files[fileId];

        if (file) {
          // Remove the file from its parent folder's childFileIds
          const parentFolder = library.folders[file.folderId];
          if (parentFolder) {
            parentFolder.childFileIds = parentFolder.childFileIds.filter((id) => id !== fileId);
          }

          delete library.files[fileId];
        }

        // remove file processing meta
        const meta = library.fileProcessingMeta[fileId];
        if (meta) {
          const parentFolderId = meta.folderId;
          const parentFolder = library.folders[parentFolderId];
          if (parentFolder) {
            parentFolder.childUploadIds = parentFolder.childUploadIds.filter((id) => id !== fileId);
          }

          delete library.fileProcessingMeta[fileId];
        }

        if (state.previewedFile?.id === fileId) {
          state.previewedFile = null;
        }
      })
      .addCase(signalingContentLibraryFolderDeleted, (state, action) => {
        const { id: deletedFolderId, personal } = action.payload;
        const library = getContentLibrary(state, personal);

        const deletedFolder = library.folders[deletedFolderId];

        if (deletedFolder) {
          // Remove the folder from its parent's childFolderIds
          const parentFolder = library.folders[deletedFolder.parentId];
          if (parentFolder) {
            parentFolder.childFolderIds = parentFolder.childFolderIds.filter(
              (id) => id !== deletedFolderId
            );
          }

          delete library.folders[deletedFolderId];

          // If the deleted folder was the active folder, set the parent folder as the new active folder
          if (deletedFolderId === library.activeFolderId) {
            library.activeFolderId = deletedFolder.parentId || 'root';
          }
        }
      })
      .addCase(signalingContentLibraryFileUploadAccepted, (state, action) => {
        const { id, name, folderId } = action.payload;
        const parentFolder = folderId || 'root';

        const library = state.libraries[state.activeLibraryType];

        // 'uploading' is written only here, so it marks a local transfer still in flight
        const soloUpload = !Object.values(library.fileProcessingMeta).some(
          (meta) => meta.status === 'uploading'
        );

        library.fileProcessingMeta[action.payload.id] = {
          id,
          name,
          status: 'uploading',
          progress: 0,
          folderId: parentFolder,
          personal: state.activeLibraryType === 'personal',
        };
        if (library.folders[parentFolder]) {
          library.folders[parentFolder].childUploadIds.push(id);
        }

        state.pendingReveal = soloUpload ? { id } : null;
      })
      .addCase(signalingContentLibraryFileUploaded, (state, action) => {
        handleFileProcessing(state, action.payload);

        promoteRevealById(state, action.payload.id);
      })
      .addCase(signalingWhiteboardCreated, (state, action) => {
        handleFileProcessing(state, action.payload);
      })
      .addCase(signalingWhiteboardClosed, (state, action) => {
        closeActiveWhiteboard(state, action.payload.id);
      })
      .addCase(bulkWhiteboardsClosed, (state, action) => {
        for (const id of action.payload) {
          closeActiveWhiteboard(state, id);
        }
      })
      .addCase(createEmbedContentRequested, (state, action) => {
        const { name, folderId, personal } = action.payload;

        state.pendingReveal = { name, parentFolderId: folderId || 'root', personal };
      })
      .addCase(signalingContentLibraryEmbedContentCreated, (state, action) => {
        const { personal } = action.payload;
        const library = getContentLibrary(state, personal);
        const file = normalizeFile(action.payload);
        library.files[file.id] = file;

        const parentFolder = library.folders[file.folderId];
        if (parentFolder) {
          parentFolder.childFileIds.push(file.id);
        }

        promoteRevealByLocation(state, {
          id: file.id,
          name: file.name,
          parentFolderId: file.folderId,
          personal: file.personal,
        });
      })
      .addCase(signalingContentLibraryFileConverted, (state, action) => {
        const { id, personal } = action.payload;
        const library = getContentLibrary(state, personal);
        const file = library.files[id];
        if (file) {
          file.status = 'completed';
        }

        const processingMeta = library.fileProcessingMeta[id];
        if (processingMeta) {
          delete library.fileProcessingMeta[id];
        }
      })
      .addCase(signalingContentLibraryConversionFailed, (state, action) => {
        const library = getContentLibrary(state, action.payload.personal);
        const processingMeta = library.fileProcessingMeta[action.payload.id];
        if (processingMeta) {
          processingMeta.status = 'conversion-failed';
        }
      })
      .addCase(signalingContentLibraryConversionProgress, (state, action) => {
        const { id, percentage, personal } = action.payload;
        const library = getContentLibrary(state, personal);
        const processingMeta = library.fileProcessingMeta[id];
        if (processingMeta && percentage > processingMeta.progress) {
          processingMeta.progress = percentage;
        }
      })
      .addCase(signalingContentLibraryFileClosed, (state, action) => {
        const { id: fileId } = action.payload;
        closeActiveFile(state, fileId);

        if (state.mediaPlayers[fileId]) {
          delete state.mediaPlayers[fileId];
        }
      })
      .addCase(bulkFilesClosed, (state, action) => {
        for (const id of action.payload) {
          closeActiveFile(state, id);

          if (state.mediaPlayers[id]) {
            delete state.mediaPlayers[id];
          }
        }
      })
      .addCase(whiteboardViewed, (state, action) => {
        const whiteboardId = action.payload;

        if (state.activeWhiteboards[whiteboardId]) {
          state.activeWhiteboards[whiteboardId] = {
            ...state.activeWhiteboards[whiteboardId],
            date: new Date().toISOString(),
          };
        }
      })
      .addCase(fileViewed, (state, action) => {
        const { id: fileId } = action.payload;

        if (state.activeFiles[fileId]) {
          state.activeFiles[fileId] = {
            ...state.activeFiles[fileId],
            date: new Date().toISOString(),
          };
        }
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const { name: username } = action.meta;

        if (state.originalDefaultLibraryType === null) {
          state.originalDefaultLibraryType = action.payload.settings.defaultContentLibrary;
        }

        state.activeLibraryType = action.payload.settings.defaultContentLibrary;

        state.templateParams = {
          ...state.templateParams,
          USERNAME: username,
        };
      })
      .addCase(signalingContentLibraryOpenedFilesReceived, (state, action) => {
        action.payload.files.forEach((file) => {
          state.activeFiles[file.data.id] = file.data;
        });
      })
      .addCase(signalingContentLibraryFileShared, (state, action) => {
        const { id, targetUsers } = action.payload;

        findAndUpdateFileInLibraries(state, id, (file) => {
          file.shared = true;
        });

        if (!targetUsers) {
          state.sharedFiles.files[id] = action.payload;
          state.sharedFiles.folder.childFileIds.push(id);
        }
      })
      .addCase(signalingContentLibraryFileUnshared, (state, action) => {
        const { id } = action.payload;

        findAndUpdateFileInLibraries(state, id, (file) => {
          file.shared = false;
        });

        state.sharedFiles.folder.childFileIds = state.sharedFiles.folder.childFileIds.filter(
          (fileId) => fileId !== id
        );
        delete state.sharedFiles.files[id];

        if (
          state.previewedFile?.id === id &&
          'personal' in state.previewedFile &&
          !state.previewedFile.personal
        ) {
          state.previewedFile = null;
        }
      })
      .addCase(signalingContentLibraryFileCopied, (state, action) => {
        handleFileProcessing(state, action.payload.data);
      })
      .addCase(signalingContentLibrarySharedFilesReceived, (state, action) => {
        const { files } = action.payload;

        state.sharedFiles = normalizeSharedFiles(files);
      })
      .addCase(userRenamed, (state, action) => {
        const { id, name } = action.payload;

        for (const file of Object.values(state.sharedFiles.files)) {
          if (file.sharedBy?.id === id) {
            file.sharedBy.name = name;
          }
        }
      });
  },
});

export const {
  loaderDisplayChanged,
  folderOpened,
  fileUploadProgressChanged,
  fileOpened,
  filePageChanged,
  fileClosed,
  fileThumbnailsToggled,
  fileUploadFailed,
  embedPlayerUserAction,
  embedPlayerStateApplied,
  embedPlayerReady,
  embedPlayerDeleted,
  activeLibraryChanged,
  contentLibrarySortByChanged,
  templateParamsChanged,
  activeFilesCollectionUpdated,
  openedFileCleared,
  activeFilesCollectionCleared,
  activeWhiteboardsCollectionReplaced,
  activeWhiteboardsCollectionUpdated,
  activeWhiteboardsCollectionCleared,
  currentActiveFileUpdated,
  sharedFolderLocalized,
  sharedFilesCleared,
  previewFileOpened,
  previewFileClosed,
  previewPageChanged,
  revealItemCleared,
} = contentLibrary.actions;

export default contentLibrary.reducer;

export const selectContentLibraryAvailable = (
  state: RootState,
  libraryType: ContentLibraryType
) => {
  const { libraryId } = state.contentLibrary.libraries[libraryType];
  return libraryId !== undefined;
};

export const selectActiveLibraryType = (state: RootState) => state.contentLibrary.activeLibraryType;
export const selectContentLibrarySortBy = (state: RootState) => state.contentLibrary.sortBy;
export const selectContentLibraryRevealItemId = (state: RootState) =>
  state.contentLibrary.revealItemId;
export const selectContentWhiteboardLoader = (state: RootState) => state.contentLibrary.showLoader;
export const selectOriginalDefaultLibraryType = (state: RootState) =>
  state.contentLibrary.originalDefaultLibraryType;
export const selectActiveLibrary = (state: RootState) =>
  state.contentLibrary.libraries[state.contentLibrary.activeLibraryType];

export const selectRoomLibrary = (state: RootState) => state.contentLibrary.libraries.room;

export const selectFiles = (state: RootState) => {
  const library = selectActiveLibrary(state);

  return library.files;
};
export const selectFolders = (state: RootState) => {
  const library = selectActiveLibrary(state);

  return library.folders;
};

export const selectRootFolder = (state: RootState) => {
  const library = selectActiveLibrary(state);

  return library.folders.root;
};

export const selectFolder = (state: RootState, id: string): ContentLibraryFolder | undefined => {
  const library = selectActiveLibrary(state);

  return library.folders[id];
};

export const selectFolderByLibraryType = (
  state: RootState,
  id: string,
  libraryType: ContentLibraryType
): ContentLibraryFolder | undefined => {
  const library = state.contentLibrary.libraries[libraryType];

  return library.folders[id];
};

export const selectSharedFolder = (state: RootState) => state.contentLibrary.sharedFiles.folder;

export const selectSharedFiles = (state: RootState) => state.contentLibrary.sharedFiles;

export const selectSharedFileById = (state: RootState, id: string) =>
  state.contentLibrary.sharedFiles.files[id];

export const selectActiveFile = (
  state: RootState,
  id: string
): ContentLibraryOpenedFile | undefined => state.contentLibrary.activeFiles[id];

export const selectActiveWhiteboard = (
  state: RootState,
  id: string
): ContentLibraryOpenedWhiteboard | undefined => state.contentLibrary.activeWhiteboards[id];

export const selectIsContentLibraryItemActive = (state: RootState, id: string): boolean =>
  Boolean(state.contentLibrary.activeFiles[id] || state.contentLibrary.activeWhiteboards[id]);

export const selectFile = (state: RootState, id: string): ContentLibraryFileEntry | undefined => {
  const library = selectActiveLibrary(state);

  return library.files[id];
};

export const selectFileByLibraryType = (
  state: RootState,
  id: string,
  libraryType: ContentLibraryType
): ContentLibraryFileEntry | undefined => {
  const library = state.contentLibrary.libraries[libraryType];

  return library.files[id];
};

export const selectActiveFolderId = (state: RootState) => {
  const library = selectActiveLibrary(state);

  return library.activeFolderId;
};

export const selectContentLibraryProcessingMeta = (state: RootState) => {
  const library = selectActiveLibrary(state);

  return library.fileProcessingMeta;
};

export const selectActiveFolder = createSelector(
  [selectActiveFolderId, selectFolders, selectSharedFolder],
  (activeFolderId, folders, sharedFolder) => {
    if (activeFolderId === sharedFolder.id) {
      return sharedFolder;
    }
    return folders[activeFolderId];
  }
);

export const selectContentLibraryFileProcessingMeta = (
  state: RootState,
  id: string
): ContentLibraryFileProcessingMeta | undefined => {
  const library = selectActiveLibrary(state);

  return library.fileProcessingMeta[id];
};

export const selectContentLibraryOpenedFile = (state: RootState) =>
  state.contentLibrary.openedFile.data;

export const selectPreviewedFile = (state: RootState) => state.contentLibrary.previewedFile;

export const selectIsPreviewActive = (state: RootState) =>
  state.contentLibrary.previewedFile !== null;

export const selectIsPersonalFile = createSelector(
  [(state: RootState, id: string) => selectFileByLibraryType(state, id, 'personal')],
  (file) => file !== undefined
);

export const selectLastContentLibraryOpenedFile = (state: RootState) =>
  Object.values(state.contentLibrary.activeFiles).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )[0];

export const selectLastContentLibraryOpenedWhiteboard = (state: RootState) =>
  Object.values(state.contentLibrary.activeWhiteboards).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )[0];

export const selectContentLibraryOpenedFileCurrentPage = (state: RootState) =>
  state.contentLibrary.openedFile.currentPage;

export const selectPreviewedFileCurrentPage = createSelector(
  [selectPreviewedFile],
  (previewedFile) => previewedFile?.page ?? 0
);

export const selectActiveContentPage = createSelector(
  [
    selectContentLibraryOpenedFileCurrentPage,
    selectPreviewedFileCurrentPage,
    (_state: RootState, isPreview: boolean) => isPreview,
  ],
  (livePage, previewPage, isPreview) => (isPreview ? previewPage : livePage)
);

export const selectContentLibraryOpenedFileThumbnailsActive = (state: RootState) =>
  state.contentLibrary.openedFileThumbnailsActive;

export const selectContentLibraryActiveFiles = createSelector(
  (state: RootState) => state.contentLibrary.activeFiles,
  (files) => Object.values(files)
);

export const selectCurrentActiveFileId = (state: RootState) =>
  state.contentLibrary.currentActiveFileId;

export const selectContentLibraryActiveWhiteboards = createSelector(
  (state: RootState) => state.contentLibrary.activeWhiteboards,
  (files) => Object.values(files)
);

export const selectActivePublicWhiteboards = createSelector(
  (state: RootState) => state.contentLibrary.activeWhiteboards,
  (files) => Object.values(files).filter((file) => !file.private)
);

export const selectContentLibraryMediaPlayer = (state: RootState, id: string) => {
  const player = state.contentLibrary.mediaPlayers[id];
  if (!player) {
    return;
  }

  return player;
};

export const selectContentLibraryActiveState = createSelector(
  [selectActiveFolderId, selectActiveLibraryType],
  (activeFolderId, activeLibraryType) => ({
    activeFolderId,
    activeLibraryType,
  })
);

export const selectContentLibraryPermissions = createSelector(
  [
    (state: RootState) => selectHasPermissions(state, PermissionTypes.presentContentLibrary),
    (state: RootState) => selectCanManagePrivateWhiteboards(state),
  ],
  (canPresentLibrary, canManagePrivateWhiteboards) => ({
    canPresentLibrary,
    canManagePrivateWhiteboards,
  })
);
